import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, pushPermission, enableCustomerPush } from "../lib/push";

const SNOOZE_MS = 5 * 60 * 1000;
const key = (id) => `kheffah_push_order_${id}`;

export default function CustomerNotificationPrompt({ orderId, orderStatus }) {
  const [state, setState] = useState(null); // null | "on" | "snoozed" | "denied"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    const saved = localStorage.getItem(key(orderId));
    if (saved === "on") { setState("on"); return; }
    if (pushPermission() === "denied") { setState("denied"); return; }
    if (saved && saved.startsWith("later:")) {
      const at = Number(saved.split(":")[1] || 0);
      setState(Date.now() - at < SNOOZE_MS ? "snoozed" : null);
      return;
    }
    setState(null);
  }, [orderId]);

  const finished = orderStatus === "COMPLETED" || orderStatus === "CANCELLED";
  if (!orderId || finished || !pushSupported()) return null;
  if (state === "snoozed") return null;

  if (state === "on") {
    return (
      <div className="mt-5 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-2 text-green-800 font-bold text-sm"
        data-testid="push-enabled-state">
        <Bell size={16} /> تم تفعيل إشعارات الطلب
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="mt-5 bg-white border border-orange-100 rounded-2xl p-4 text-sm text-gray-600 flex items-start gap-2"
        data-testid="push-denied-state">
        <BellOff size={16} className="mt-0.5 text-gray-400" />
        <span>إشعارات المتصفح موقوفة لهذا الموقع. يمكنك تفعيلها من إعدادات المتصفح، وتتبع الطلب يعمل بشكل طبيعي.</span>
      </div>
    );
  }

  const activate = async () => {
    setBusy(true);
    try {
      const result = await enableCustomerPush(orderId);
      if (result === "granted") {
        localStorage.setItem(key(orderId), "on");
        setState("on");
        toast.success("تم تفعيل إشعارات الطلب");
      } else if (result === "denied") {
        setState("denied");
      } else {
        toast.error("لم يتم تفعيل الإشعارات");
      }
    } catch (e) {
      toast.error("تعذر تفعيل الإشعارات");
    } finally {
      setBusy(false);
    }
  };

  const later = () => {
    localStorage.setItem(key(orderId), `later:${Date.now()}`);
    setState("snoozed");
  };

  return (
    <div className="mt-5 bg-white rounded-2xl border border-orange-100 p-4" data-testid="push-prompt">
      <div className="font-bold flex items-center gap-2"><Bell size={18} className="text-orange-600" /> تبغى تعرف حالة طلبك أول بأول؟</div>
      <div className="text-sm text-gray-600 mt-1">فعّل إشعارات الطلب وراح نعلمك عند بدء التجهيز، جاهزية الطلب وأي تحديث مهم.</div>
      <div className="mt-3 flex gap-2">
        <button onClick={activate} disabled={busy}
          className="flex-1 pill brand-grad text-white font-extrabold py-3 tap-target disabled:opacity-60"
          data-testid="push-enable-btn">
          تفعيل الإشعارات
        </button>
        <button onClick={later} className="pill bg-white border border-gray-200 text-gray-600 font-bold px-4 py-3 tap-target"
          data-testid="push-later-btn">
          ليس الآن
        </button>
      </div>
    </div>
  );
}
